import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  FaUser,
  FaEnvelope,
  FaBuilding,
  FaIdCard,
  FaPhone,
  FaBriefcase,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUserFriends,
  FaGraduationCap,
  FaClock,
  FaUserCircle
} from 'react-icons/fa';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import './Profile.css';

const ManagerProfile = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const baseUrl = "https://api.burrayhr.com";

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseUrl}/api/profile/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setProfileData(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch profile information',
        confirmButtonColor: '#1e3a8a'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div>
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading profile information...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Topbar />
      <Sidebar />
      <div className="main-content">
        <div className="profile-container">
          <div className="profile-header">
            <div className="profile-photo-section">
              {profileData?.employee_data?.photo ? (
                <img 
                  src={profileData.employee_data.photo} 
                  alt="Profile" 
                  className="profile-photo"
                />
              ) : (
                <FaUserCircle className="default-photo" />
              )}
            </div>
            <div className="profile-title">
              <h1 style={{color: '#fff'}}>{profileData?.employee_data?.full_name}</h1>
              <p style={{color: 'lightgray'}} className="role"><span style={{fontWeight: 'bold'}}>Position:</span> {profileData?.employee_data?.position}</p>
              <div className="profile-id">
                <span style={{color: 'lightgray', borderRadius: '5px', padding: '5px', border: '1px solid lightgray'}}>Employee ID: {profileData?.employee_data?.employee_id || 'Not assigned'}</span>
              </div>
            </div>
          </div>

          <div className="profile-content">
            <div className="info-section">
              <h2>Basic Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <FaEnvelope className="info-icon" />
                  <div className="info-text">
                    <label>Email</label>
                    <p style={{fontSize: '13px'}}>{profileData?.email}</p>
                  </div>
                </div>
                
                <div className="info-item">
                  <FaPhone className="info-icon" />
                  <div className="info-text">
                    <label>Phone Number</label>
                    <p>{profileData?.employee_data?.phone_number || 'Not provided'}</p>
                  </div>
                </div>

                <div className="info-item">
                  <FaBuilding className="info-icon" />
                  <div className="info-text">
                    <label>Department</label>
                    <p>{profileData?.employee_data?.department || 'Not assigned'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
          .profile-container {
            padding: 2rem;
            max-width: 1200px;
            margin: 0 auto;
          }

          .profile-header {
            display: flex;
            align-items: center;
            gap: 2rem;
            margin-bottom: 2rem;
            background-color: white;
            padding: 2rem;
            border-radius: 0.75rem;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }

          .profile-photo-section {
            flex-shrink: 0;
          }

          .profile-photo {
            width: 150px;
            height: 150px;
            border-radius: 50%;
            object-fit: cover;
            border: 3px solid #e5e7eb;
          }

          .default-photo {
            width: 150px;
            height: 150px;
            color: #9ca3af;
          }

          .profile-title {
            flex-grow: 1;
          }

          .profile-title h1 {
            margin: 0;
            color: #1f2937;
            font-size: 1.875rem;
            font-weight: 600;
          }

          .role {
            margin: 0.5rem 0;
            font-size: 1.125rem;
          }

          .profile-id {
            margin-top: 1rem;
          }

          .info-section {
            background-color: white;
            padding: 2rem;
            border-radius: 0.75rem;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }

          .info-section h2 {
            color: #1f2937;
            font-size: 1.5rem;
            font-weight: 600;
            margin: 0 0 1.5rem 0;
          }

          .info-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
            gap: 0.5rem;
          }

          .info-item {
            display: flex;
            align-items: flex-start;
            gap: 1rem;
            padding: 1rem;
            background-color: #f9fafb;
            border-radius: 0.5rem;
            transition: transform 0.2s ease;
          }

          .info-item:hover {
            transform: translateY(-2px);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          }

          .info-icon {
            color: #1e3a8a;
            font-size: 1.25rem;
            flex-shrink: 0;
            margin-top: 0.25rem;
          }

          .info-text {
            flex-grow: 1;
          }

          .info-text label {
            display: block;
            color: #6b7280;
            font-size: 0.875rem;
            margin-bottom: 0.25rem;
          }

          .info-text p {
            margin: 0;
            color: #1f2937;
            font-size: 1rem;
          }

          .capitalize {
            text-transform: capitalize;
          }

          @media (max-width: 768px) {
            .profile-container {
              padding: 1rem;
            }

            .profile-header {
              flex-direction: column;
              text-align: center;
              padding: 1.5rem;
              gap: 1rem;
            }

            .profile-photo, .default-photo {
              width: 120px;
              height: 120px;
            }

            .profile-title h1 {
              font-size: 1.5rem;
            }

            .info-section {
              padding: 1.5rem;
            }

            .info-grid {
              grid-template-columns: 1fr;
              gap: 1rem;
            }

            .info-item {
              padding: 0.75rem;
            }
          }

          @media (max-width: 480px) {
            .profile-photo, .default-photo {
              width: 100px;
              height: 100px;
            }

            .profile-title h1 {
              font-size: 1.25rem;
            }

            .role {
              font-size: 1rem;
            }

            .info-section h2 {
              font-size: 1.25rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ManagerProfile;